import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';

export type AlertVariant = 'success' | 'info' | 'warning' | 'error';

export interface AlertProps {
  children?: React.ReactNode;
  variant?: AlertVariant;
  type?: AlertVariant; // For backward compatibility
  title?: string;
  message?: string; // For backward compatibility
  className?: string;
  closable?: boolean;
  onClose?: () => void;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  bordered?: boolean;
  filled?: boolean;
}

const Alert: React.FC<AlertProps> = ({
  children,
  variant,
  type,
  title,
  message,
  className,
  closable = false,
  onClose,
  icon,
  action,
  bordered = true,
  filled = false,
}) => {
  // Use type prop for backward compatibility if variant is not provided
  const finalVariant = variant || type || 'info';
  // Use message prop for backward compatibility if children is not provided
  const content = children || message;

  const variantConfig = {
    success: {
      icon: CheckCircle,
      classes: {
        default: 'bg-green-50 text-green-800 border-green-200',
        filled: 'bg-green-500 text-white border-green-500',
        icon: 'text-green-400',
        iconFilled: 'text-green-100',
      },
    },
    info: {
      icon: Info,
      classes: {
        default: 'bg-blue-50 text-blue-800 border-blue-200',
        filled: 'bg-blue-500 text-white border-blue-500',
        icon: 'text-blue-400',
        iconFilled: 'text-blue-100',
      },
    },
    warning: {
      icon: AlertTriangle,
      classes: {
        default: 'bg-yellow-50 text-yellow-800 border-yellow-200',
        filled: 'bg-yellow-500 text-white border-yellow-500',
        icon: 'text-yellow-400',
        iconFilled: 'text-yellow-100',
      },
    },
    error: {
      icon: AlertCircle,
      classes: {
        default: 'bg-red-50 text-red-800 border-red-200',
        filled: 'bg-red-500 text-white border-red-500',
        icon: 'text-red-400',
        iconFilled: 'text-red-100',
      },
    },
  };

  const config = variantConfig[finalVariant];
  const IconComponent = icon || config.icon;
  const iconClasses = filled ? config.classes.iconFilled : config.classes.icon;
  const alertClasses = filled ? config.classes.filled : config.classes.default;

  return (
    <div
      className={cn(
        'relative p-4 rounded-lg',
        bordered && 'border',
        alertClasses,
        className
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn('flex-shrink-0 mt-0.5', iconClasses)}>
          {React.isValidElement(IconComponent) ? (
            IconComponent
          ) : (
            <IconComponent size={20} />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className="font-medium mb-1">{title}</h4>
          )}
          <div className="text-sm">{content}</div>
        </div>

        {/* Action */}
        {action && (
          <div className="flex-shrink-0 ml-3">
            {action}
          </div>
        )}

        {/* Close Button */}
        {closable && (
          <button
            onClick={onClose}
            className={cn(
              'flex-shrink-0 ml-3 p-1 rounded-md transition-colors',
              filled
                ? 'hover:bg-white hover:bg-opacity-20'
                : 'hover:bg-black hover:bg-opacity-10'
            )}
            aria-label="Close alert"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert; 